import React, { useEffect, useState, useCallback } from 'react';
import { Button, Card, Typography, Modal, Empty, Spin, message, Tooltip, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Icon from '../../../shared/components/Icon';
import { FaRegEdit } from 'react-icons/fa';
import { VscRunCoverage } from 'react-icons/vsc';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';

const { Title, Text, Paragraph } = Typography;

const QueriesListContent = () => {
  const { team } = useParams();
  const navigate = useNavigate();

  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchQueries = useCallback(async () => {
    try {
      setLoading(true);
      // Get team context from localStorage like other pages do
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        console.error('No team selected');
        message.error('Please select a team first');
        setLoading(false);
        return;
      }
      
      const res = await axiosInstance.get(`/query/saved/team/${teamId}`);
      setQueries(res.data.queries || []);
    } catch (error) {
      console.error('Error fetching saved queries:', error);
      message.error('Failed to fetch saved queries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const handleCreateNew = () => {
    navigate(`/${team}/custom-dashboard?qs=studio`);
  };

  const handleEdit = (id) => {
    navigate(`/${team}/custom-dashboard?qs=studio&queryId=${encodeURIComponent(id)}`);
  };

  const handleRun = (id) => {
    navigate(`/${team}/custom-dashboard?qs=studio&queryId=${encodeURIComponent(id)}&run=true`);
  };

  const confirmDelete = (query) => {
    setQueryToDelete(query);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!queryToDelete) return;
    try {
      setDeleting(true);
      await axiosInstance.delete(`/query/${queryToDelete._id}`);
      message.success('Query deleted successfully');
      setDeleteModalVisible(false);
      setQueryToDelete(null);
      fetchQueries();
    } catch (error) {
      console.error('Failed to delete query:', error);
      message.error('Failed to delete query');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setQueryToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="flex-1 px-6 pt-6 overflow-auto min-w-0" style={{ backgroundColor: '#ffffff' }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Title level={2} className="mb-2" style={{ color: '#1d0c46', fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
              Saved Queries
            </Title>
            <Text style={{ color: '#666666', fontFamily: 'Raleway, sans-serif', fontSize: '16px' }}>
              View, run, and manage your saved queries
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateNew}
            size="large"
            style={{ backgroundColor: '#1d0c46', borderColor: '#1d0c46', fontFamily: 'Raleway, sans-serif', fontWeight: 500, borderRadius: '0px' }}
          >
            Create New Query
          </Button>
        </div>

        <Spin spinning={loading}>
          {queries.length === 0 ? (
            <Card className="text-center py-12 glass-effect">
              <Empty
                description={
                  <div>
                    <Title level={4} style={{ color: '#1d0c46', fontFamily: 'Raleway, sans-serif', fontWeight: 500 }}>No queries yet</Title>
                    <Paragraph style={{ color: '#666666', fontFamily: 'Raleway, sans-serif', fontSize: '16px' }}>
                      Create your first query to get started
                    </Paragraph>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew} size="large" style={{ backgroundColor: '#1d0c46', borderColor: '#1d0c46', fontFamily: 'Raleway, sans-serif', fontWeight: 500, borderRadius: '0px' }}>
                      Create Query
                    </Button>
                  </div>
                }
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {queries.map((q) => (
                <Card
                  key={q._id}
                  hoverable
                  className="dashboard-card glass-effect"
                  style={{
                    borderRadius: '0px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1px solid #DDE2EE'
                  }}
                  styles={{ body: { padding: '24px' } }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  {/* Header with title */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <Title level={4} className="mb-0" style={{ 
                        color: '#0f172a',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '1.2'
                      }}>
                        {q.name}
                      </Title>
                    </div>
                  </div>

                  {/* Description */}
                  {q.description && (
                    <div className="mb-4">
                      <Text style={{ 
                        color: '#666666',
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '14px',
                        lineHeight: '1.4',
                        display: 'block'
                      }}>
                        {q.description}
                      </Text>
                    </div>
                  )}

                  {/* Metadata section */}
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="calendar" size={16} color="#475569" />
                        <Text style={{ 
                          color: '#475569',
                          fontFamily: 'Raleway, sans-serif',
                          fontSize: '13px'
                        }}>
                          Updated {formatDate(q.updatedAt || q.createdAt)}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <Button
                      type="primary"
                      icon={<VscRunCoverage size={14} />}
                      onClick={() => handleRun(q._id)}
                      size="small"
                      style={{ 
                        backgroundColor: '#1d0c46',
                        borderColor: '#1d0c46',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                    >
                      Run
                    </Button>
                    <Button
                      icon={<FaRegEdit size={14} />}
                      onClick={() => handleEdit(q._id)}
                      size="small"
                      style={{ 
                        color: '#1d0c46',
                        borderColor: '#1d0c46',
                        backgroundColor: 'transparent',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      icon={<Icon name="delete" size={14} />}
                      onClick={() => confirmDelete(q)}
                      size="small"
                      style={{ 
                        color: '#666666',
                        borderColor: '#DDE2EE',
                        backgroundColor: 'transparent',
                        fontFamily: 'Raleway, sans-serif',
                        fontWeight: 500,
                        borderRadius: '0px',
                        height: '32px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#000000';
                        e.currentTarget.style.borderColor = '#000000';
                        e.currentTarget.style.backgroundColor = '#F5F5F5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#666666';
                        e.currentTarget.style.borderColor = '#DDE2EE';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Spin>
      </div>

      <Modal
        title={
          <span style={{ 
            color: '#1d0c46',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600
          }}>
            Delete Query
          </span>
        }
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleting}
        okText="Delete"
        cancelText="Cancel"
        width={400}
        style={{ top: 20 }}
        styles={{
          content: {
            borderRadius: 0,
            padding: 0
          },
          header: {
            borderRadius: 0,
            borderBottom: '1px solid #E5E7EB',
            padding: '16px 24px',
            margin: 0
          },
          body: { 
            padding: '20px 24px',
            backgroundColor: '#ffffff',
            borderRadius: 0
          },
          footer: {
            borderRadius: 0,
            borderTop: '1px solid #E5E7EB',
            padding: '12px 24px'
          }
        }}
        okButtonProps={{
          className: "transition-all duration-200 hover:opacity-90",
          style: {
            backgroundColor: '#1d0c46',
            borderColor: '#1d0c46',
            border: 'none',
            color: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '0px',
            padding: '6px 12px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }
        }}
        cancelButtonProps={{
          className: "transition-all duration-200 hover:opacity-90",
          style: {
            color: '#0f172a',
            borderColor: '#E5E7EB',
            borderWidth: '1px',
            borderStyle: 'solid',
            backgroundColor: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '0px',
            padding: '6px 12px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }
        }}
      >
        <p style={{ 
          fontFamily: 'Raleway, sans-serif',
          fontSize: '16px',
          color: '#0f172a',
          margin: 0
        }}>
          Are you sure you want to delete the query "{queryToDelete?.name}"? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default QueriesListContent;


